import {
  isTypingOrOverlayOpen,
  setShortcutAvailable,
  setRowShortcutNoun,
} from "@/mixins/keyboardShortcutGuards";

const ACTIVE_ROW_CLASS = "kbd-active-row";
// A table with fixed columns renders its rows once per pane; the same index is
// the same record in each.
const BODY_SELECTOR =
  ".el-table__body-wrapper, .el-table__fixed-body-wrapper";

// Keyboard navigation for a list's table:
//   Down / Up  move the highlighted row
//   Enter      open the highlighted row
//   Space      tick / untick its checkbox (lists that have one)
//   Escape     clear the highlight
// Paused while typing in a field or when a dialog or menu is open.
export default {
  mounted() {
    this.kbdRowIndex = -1;
    window.addEventListener("keydown", this.onRowShortcutKeydown);
    // Components name their rows with the `rowShortcutNoun` option.
    setRowShortcutNoun(this.$options.rowShortcutNoun || "row");
    setShortcutAvailable("rows", true);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onRowShortcutKeydown);
    setRowShortcutNoun("");
    setShortcutAvailable("rows", false);
  },
  methods: {
    // One array of <tr> per pane of the first visible table.
    getRowShortcutPanes() {
      const table = Array.from(this.$el.querySelectorAll(".el-table")).find(
        (el) => el.getClientRects().length > 0
      );
      if (!table) return [];
      return Array.from(table.querySelectorAll(BODY_SELECTOR))
        .map((body) => Array.from(body.querySelectorAll("tbody > tr.el-table__row")))
        .filter((rows) => rows.length);
    },
    highlightRowShortcut(panes, index) {
      panes.forEach((rows) =>
        rows.forEach((row, i) =>
          row.classList.toggle(ACTIVE_ROW_CLASS, i === index)
        )
      );
      this.kbdRowIndex = index;
      const row = index >= 0 && panes[0] && panes[0][index];
      if (row && row.scrollIntoView) {
        row.scrollIntoView({ block: "nearest" });
      }
    },
    // The first visible match for a selector in the highlighted row, whichever pane holds it.
    findInActiveRow(panes, selector) {
      for (const rows of panes) {
        const row = rows[this.kbdRowIndex];
        const match =
          row &&
          Array.from(row.querySelectorAll(selector)).find(
            (el) => el.getClientRects().length > 0
          );
        if (match) return match;
      }
      return null;
    },
    onRowShortcutKeydown(event) {
      const key = event.key;
      if (!["ArrowDown", "ArrowUp", "Enter", " ", "Escape"].includes(key)) return;
      if (event.defaultPrevented) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTypingOrOverlayOpen(event.target)) return;

      const panes = this.getRowShortcutPanes();
      const rowCount = panes.length ? panes[0].length : 0;
      if (!rowCount) return;
      // The page may have re-rendered with fewer rows since the last key press.
      if (this.kbdRowIndex >= rowCount) this.kbdRowIndex = -1;

      if (key === "Escape") {
        this.highlightRowShortcut(panes, -1);
        return;
      }

      if (key === "ArrowDown" || key === "ArrowUp") {
        event.preventDefault();
        const step = key === "ArrowDown" ? 1 : -1;
        const start = this.kbdRowIndex === -1 && step === -1 ? rowCount : this.kbdRowIndex;
        const next = Math.max(0, Math.min(rowCount - 1, start + step));
        this.highlightRowShortcut(panes, next);
        return;
      }

      // Enter / Space act on the highlighted row only, and leave a focused
      // button or link to do its own thing.
      if (this.kbdRowIndex === -1) return;
      const active = document.activeElement;
      const focusedControl =
        active && active !== document.body && active.closest("button, a, [role='button']");
      if (focusedControl && !focusedControl.closest(".el-table")) return;

      if (key === "Enter") {
        const openSelector =
          this.$options.rowShortcutOpenSelector ||
          "#accession-id, .listings__action-buttons li";
        const target = this.findInActiveRow(panes, openSelector);
        if (target) {
          event.preventDefault();
          target.click();
        }
        return;
      }

      // Space
      const checkbox = this.findInActiveRow(
        panes,
        ".el-table-column--selection .el-checkbox"
      );
      if (checkbox) {
        event.preventDefault();
        checkbox.click();
      }
    },
  },
};
